import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import DisplaySignOut from "./ActiveSignOuts";
import SignOutForm from "./SignOutForm";
import NameSignOutGrid from "./NameSignOutGrid";
import { FollowedSignOuts } from "./FollowedSignOuts";
import ClassroomModeTip from "./ClassroomModeTip";
import { useUser } from "../shared/UserContext";
import useFetchClasses from "../hooks/useTeacherClasses";
import "./Attendance.css";

function ClassSignOutPage() {
  const { user, uid, loading, specialistMode } = useUser();
  const { classList } = useFetchClasses();
  const [nameMode, setNameMode] = useState(false);
  const [requireIdOnReturn, setRequireIdOnReturn] = useState(false);
  const [room, setRoom] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");

  const classKey = uid ? `signout-context-class-${uid}` : null;

  // Mark the dashboard onboarding's "Open the sign-out screen" step complete.
  // (Dashboard reads this flag; without a writer the checklist never finished.)
  useEffect(() => {
    if (uid) localStorage.setItem(`classboon_onboarding_signout_${uid}`, "true");
  }, [uid]);

  // Room is teacher-set in Settings (signOutRoom) — never editable from this
  // student-facing screen. Read it live alongside the sign-out mode flags.
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(firestore, "users", uid), snap => {
      const data = snap.data();
      setNameMode(data?.nameSignOut === true);
      setRequireIdOnReturn(data?.signInRequiresId === true);
      setRoom(data?.signOutRoom || "");
    });
  }, [uid]);

  // Active (non-archived) classes — the ones you'd sign students out from.
  const activeClasses = classList.filter(c => !c.isArchived);

  // Pick the class whose roster the name-grid shows.
  useEffect(() => {
    if (!activeClasses.length) return;
    setSelectedClassId(prev => {
      if (prev && activeClasses.some(c => c.id === prev)) return prev;
      const saved = classKey ? localStorage.getItem(classKey) : null;
      if (saved && activeClasses.some(c => c.id === saved)) return saved;
      return activeClasses[0].id;
    });
  }, [activeClasses, classKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectClass = (id) => {
    setSelectedClassId(id);
    if (classKey) localStorage.setItem(classKey, id);
  };

  if (loading) return <div>Loading...</div>;

  const selectedClass = classList.find(c => c.id === selectedClassId);
  // Specialists always use the name-grid (pick any class → tap a kid), even if they
  // never turned on name sign-out for a homeroom of their own.
  const useNameGrid = nameMode || specialistMode;

  return (
    <div className="content-container" data-tour="signout-grid">
      <ClassroomModeTip />
      {useNameGrid ? (
        <div className="attendance-container signout-name-mode" style={{ marginBottom: 0 }}>
          {activeClasses.length > 1 ? (
            <>
              {specialistMode && (
                <label className="settings-label" style={{ display: "block", marginBottom: 6 }}>
                  Choose a classroom
                </label>
              )}
              <select
                className="class-select"
                value={selectedClassId}
                onChange={e => selectClass(e.target.value)}
              >
                {activeClasses.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.className}</option>
                ))}
              </select>
            </>
          ) : (
            <h2 style={{ marginBottom: 20 }}>{selectedClass?.className}</h2>
          )}
          {selectedClassId && (
            <NameSignOutGrid classId={selectedClassId} requireIdOnReturn={requireIdOnReturn} />
          )}
        </div>
      ) : (
        <div className="split-layout">
          <div className="split-form">
            <SignOutForm user={user} room={room} />
          </div>
          <div className="split-list">
            <DisplaySignOut user={user} requireIdOnReturn={requireIdOnReturn} />
            <FollowedSignOuts />
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassSignOutPage;
